// @flow
import * as React from 'react'

type Props = {
  children: React.Node,
  onScrollToBottom: () => void,
}

const OFFSET = 30

function InfiniteScroller({ children, onScrollToBottom }: Props): React.Node {
  const scrollRef = React.useRef(null)

  const handleScroll = React.useCallback(() => {
    if (!scrollRef.current) return

    const { scrollHeight, scrollTop, clientHeight } = scrollRef.current

    if (scrollTop + clientHeight >= scrollHeight - OFFSET) {
      onScrollToBottom()
    }
  }, [onScrollToBottom])

  const isScrollable = React.useCallback(() => {
    if (!scrollRef.current) return false

    const { scrollHeight, clientHeight } = scrollRef.current

    return scrollHeight > clientHeight
  }, [])

  const onResize = React.useCallback(() => {
    if (isScrollable()) return
    onScrollToBottom()
  }, [isScrollable, onScrollToBottom])

  React.useEffect(() => {
    const scrollElement = scrollRef.current
    if (!scrollElement) return

    scrollElement.addEventListener('scroll', handleScroll)

    const resizeObserver = new ResizeObserver(onResize)
    resizeObserver?.observe(scrollElement)

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll)
      resizeObserver?.disconnect()
    }
  }, [handleScroll, onResize])

  return (
    <div className="overflow-y-auto" ref={scrollRef}>
      {children}
    </div>
  )
}

export default InfiniteScroller
